import type { CreditOffer } from '@/features/offer/types';
import type { PreliminaryChoice } from '@/features/offer/hooks/preliminaryChoice';
import {
  calculateCreditLineOfferDetails,
  calculateInstallmentLoanDetails,
} from '@/features/offer/hooks/loanOfferUtils';
import type { OfferChanges } from '../../components/FinalOfferChangesBanner';

export type ChosenOfferNext =
  | {
      kind: 'confirm';
      type: PreliminaryChoice['type'];
      amount: number;
      numberOfRates?: number;
      isDerogation: boolean;
      changes?: OfferChanges;
    }
  | { kind: 'alternative' };

interface ResolveChosenOfferParams {
  choice: PreliminaryChoice;
  creditLineOffer: CreditOffer | undefined;
  installmentOffer: CreditOffer | undefined;
  preliminaryOffers: CreditOffer[] | undefined;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// DAE as shown: one decimal.
const roundRate = (rate: number) => Math.round(rate * 10) / 10;

/**
 * Where the final offers continue for a user who went to KYC with a chosen
 * preliminary credit: its confirmation with the chosen amount (within the
 * final limits, with what ANAF changed), or the other credit when the chosen
 * one has no final offer.
 */
export const resolveChosenOffer = ({
  choice,
  creditLineOffer,
  installmentOffer,
  preliminaryOffers,
}: ResolveChosenOfferParams): ChosenOfferNext | null => {
  const isCreditLine = choice.type === 'credit-line';
  const chosen = isCreditLine ? creditLineOffer : installmentOffer;

  if (!chosen) {
    return creditLineOffer || installmentOffer ? { kind: 'alternative' } : null;
  }

  const amount = clamp(choice.amount, chosen.minAmount ?? 0, chosen.creditLimit);
  const numberOfRates = isCreditLine
    ? undefined
    : clamp(
        choice.numberOfRates ?? chosen.maxNumberOfRates,
        chosen.minNumberOfRates ?? 1,
        chosen.maxNumberOfRates
      );

  // The backend keeps the preliminary offers next to the final ones; the
  // choice keeps them too, for when it does not.
  const preliminary =
    preliminaryOffers?.find((offer) => offer.type === chosen.type) ?? choice.offer;
  const isChanged =
    preliminary.creditLimit !== chosen.creditLimit ||
    roundRate(preliminary.effectiveAnnualPercentageRate) !==
      roundRate(chosen.effectiveAnnualPercentageRate);

  const isDerogation = isCreditLine
    ? !!calculateCreditLineOfferDetails(chosen, { amount })?.isDerogation
    : !!calculateInstallmentLoanDetails(chosen, amount, numberOfRates)?.isDerogation;

  return {
    kind: 'confirm',
    type: choice.type,
    amount,
    numberOfRates,
    isDerogation,
    changes: isChanged
      ? {
          amountLabel: isCreditLine ? 'Limita' : 'Suma',
          previousAmount: preliminary.creditLimit,
          amount: chosen.creditLimit,
          previousRate: roundRate(preliminary.effectiveAnnualPercentageRate),
          rate: roundRate(chosen.effectiveAnnualPercentageRate),
        }
      : undefined,
  };
};
